import { MouseEvent, forwardRef } from "react";
import { Button, ButtonProps } from "../Button";
import { DataTrackingId } from "../../types";
import { childrenToString } from "../../internal/functions";
import { useTrackingId } from "../../hooks";
import { useDrawerContext } from "./internal/useDrawerContext";

/**
 * Props for the DrawerCancelButton component
 * @extends ButtonProps
 */
export type DrawerCancelButtonProps = ButtonProps & DataTrackingId;

/**
 * A button component that automatically closes the drawer when clicked.
 *
 * Features:
 * - Automatically closes the drawer when clicked
 * - Extends Button component functionality
 * - Accessible with proper ARIA attributes
 * - Consistent styling with the design system
 * - Supports all standard button props
 * - Proper focus management
 * - Can be used anywhere within the drawer context
 *
 * @example
 * <DrawerCancelButton appearance="ghost" size="small">
 *   Cancel
 * </DrawerCancelButton>
 */
export const DrawerCancelButton = forwardRef<
  HTMLButtonElement,
  DrawerCancelButtonProps
>((props, ref) => {
  const { onClick, ...rest } = props;
  const { close } = useDrawerContext();
  const data = {
    children: childrenToString(props.children),
    appearance: props.appearance,
    size: props.size,
    type: props.type,
  };

  const trackingId = useTrackingId({
    name: "DrawerCancelButton",
    data,
    hasOverride: !!props["data-tracking-id"],
  });

  const onClickHandler = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e);
    close();
  };
  return (
    <Button
      aria-label="Close drawer"
      data-anv="drawer-cancel-button"
      data-tracking-id={trackingId}
      onClick={onClickHandler}
      ref={ref}
      {...rest}
    />
  );
});

DrawerCancelButton.displayName = "DrawerCancelButton";
